import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaUsers, FaBuilding, FaCog, FaShieldAlt,
  FaUserPlus, FaFileAlt,
  FaCheckCircle, FaArrowUp, FaArrowDown,
  FaQuestionCircle, FaChartLine, FaClipboardCheck,
  FaTasks, FaClock, FaSpinner, FaChartBar, FaChartPie
} from "react-icons/fa";
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const HomeForSuperAdmin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState({
    users: {
      total: 0,
      companies: 0,
      regularUsers: 0,
      monthlyGrowth: 0,
      showMonthlyGrowth: false
    },
    companies: {
      total: 0,
      active: 0,
      weeklyGrowth: 0,
      showWeeklyGrowth: false,
      recent: 0
    },
    questions: {
      total: 0,
      assigned: 0,
      answered: 0,
      inProgress: 0,
      completionRate: 0
    },
    answers: {
      total: 0,
      byType: {
        true: 0,
        false: 0,
        progress: 0,
        text: 0
      }
    },
    activity: {
      recentAnswers: 0,
      recentAssignments: 0
    },
    disclosureTopics: {
      total: 0,
      byStandard: {}
    }
  });
  const [loading, setLoading] = useState(false);

  const quickActions = useMemo(() => [
    { id: 1, title: t('superAdmin.addNewUser'), icon: <FaUserPlus className="w-5 h-5 sm:w-6 sm:h-6" />, color: 'blue', action: 'addUser' },
    { id: 2, title: t('superAdmin.adminList'), icon: <FaBuilding className="w-5 h-5 sm:w-6 sm:h-6" />, color: 'green', action: 'adminList' },
    { id: 3, title: t('superAdmin.analytics'), icon: <FaFileAlt className="w-5 h-5 sm:w-6 sm:h-6" />, color: 'purple', action: 'generateReports' },
    { id: 4, title: t('superAdmin.addQuestion'), icon: <FaQuestionCircle className="w-5 h-5 sm:w-6 sm:h-6" />, color: 'red', action: 'addQuestion' },
    { id: 5, title: t('superAdmin.systemSettings'), icon: <FaCog className="w-5 h-5 sm:w-6 sm:h-6" />, color: 'orange', action: 'systemSettings' }
  ], [t]);

  const handleQuickAction = (action) => {
    switch (action) {
      case 'addUser':
        // Navigate to users page with modal parameter
        navigate('/users?openModal=true');
        break;
      case 'adminList':
        // Navigate to users page with companyAdmin filter
        navigate('/users?role=companyAdmin');
        break;
      case 'generateReports':
        // Navigate to reporting page
        navigate('/reporting');
        break;
      case 'addQuestion':
        // Navigate to questions-management page with modal open
        navigate('/questions-management?openModal=true');
        break;
      case 'systemSettings':
        // Navigate to profile page
        navigate('/profile');
        break;
      default:
        toastr.info(t('superAdmin.actionNotImplemented'));
    }
  };


  // Fetch dashboard stats dynamically
  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/dashboard/stats`);
      
      if (response.data?.success && response.data?.stats) {
        setDashboardData(response.data.stats);
      } else {
        console.error("Invalid response format:", response.data);
        // Set default stats on error
        setDashboardData({
          users: { total: 0, companies: 0, regularUsers: 0, monthlyGrowth: 0, showMonthlyGrowth: false },
          companies: { total: 0, active: 0, weeklyGrowth: 0, showWeeklyGrowth: false, recent: 0 },
          questions: { total: 0, assigned: 0, answered: 0, inProgress: 0, completionRate: 0 },
          answers: { total: 0, byType: { true: 0, false: 0, progress: 0, text: 0 } },
          activity: { recentAnswers: 0, recentAssignments: 0 },
          disclosureTopics: { total: 0, byStandard: {} }
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      // Set default stats on error
      setDashboardData({
        users: { total: 0, companies: 0, regularUsers: 0, monthlyGrowth: 0, showMonthlyGrowth: false },
        companies: { total: 0, active: 0, weeklyGrowth: 0, showWeeklyGrowth: false, recent: 0 },
        questions: { total: 0, assigned: 0, answered: 0, inProgress: 0, completionRate: 0 },
        answers: { total: 0, byType: { true: 0, false: 0, progress: 0, text: 0 } },
        activity: { recentAnswers: 0, recentAssignments: 0 },
        disclosureTopics: { total: 0, byStandard: {} }
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard stats on component mount
  useEffect(() => {
    fetchDashboardStats();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Compact Header Section */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-lg relative overflow-hidden">
        <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-4 relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow">
                <FaShieldAlt className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">{t('superAdmin.superAdminDashboard')}</h1>
                <p className="text-green-100 text-sm">{t('superAdmin.systemOverview')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-100">{t('superAdmin.lastUpdated')}: {new Date().toLocaleDateString()}</p>
              <p className="text-sm text-lime-300 font-bold">{dashboardData.users?.total || 0} {t('superAdmin.totalUsers')}</p>
              <p className="text-xs text-green-200">{t('superAdmin.activeCompanies')}: {dashboardData.companies?.active || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl 2xl:max-w-[90rem] 3xl:max-w-[100rem] 4xl:max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <FaSpinner className="w-8 h-8 text-green-600 animate-spin" />
          </div>
        ) : (
          <>
            {/* Compact Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Total Users Card */}
              <div className="bg-white rounded-lg shadow border border-green-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('superAdmin.totalUsers')}</p>
                    <p className="text-2xl font-bold text-blue-600">{(dashboardData.users?.total || 0).toLocaleString()}</p>
                     <p className="text-xs text-gray-500 mt-1">{t('superAdmin.active')}: {(dashboardData.users?.active || 0).toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaUsers className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              {/* Total Companies Card */}
              <div className="bg-white rounded-lg shadow border border-green-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('superAdmin.totalCompanies')}</p>
                    <p className="text-2xl font-bold text-green-600">{dashboardData.companies?.total || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('superAdmin.active')}: {dashboardData.companies?.active || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <FaBuilding className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>

              {/* Total Questions Card */}
              <div className="bg-white rounded-lg shadow border border-green-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('superAdmin.totalQuestions')}</p>
                    <p className="text-2xl font-bold text-purple-600">{(dashboardData.questions?.total || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('superAdmin.assigned')}: {(dashboardData.questions?.assigned || 0).toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <FaQuestionCircle className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </div>

            </div>

            {/* Secondary Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* In Progress Questions Card */}
              <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('superAdmin.inProgress')}</p>
                    <p className="text-2xl font-bold text-orange-600">{(dashboardData.questions?.inProgress || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('superAdmin.questionsPending')}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <FaClock className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* Recent Activity Card */}
              <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('superAdmin.recentActivity')}</p>
                    <p className="text-2xl font-bold text-pink-600">{(dashboardData.activity?.recentAnswers || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('superAdmin.answersLast7Days')}</p>
                  </div>
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                    <FaChartLine className="w-6 h-6 text-pink-600" />
                  </div>
                </div>
              </div>

              {/* Disclosure Topics Card */}
              <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('superAdmin.disclosureTopics')}</p>
                    <p className="text-2xl font-bold text-cyan-600">{(dashboardData.disclosureTopics?.total || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('superAdmin.activeTopics')}</p>
                  </div>
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                    <FaTasks className="w-6 h-6 text-cyan-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Quick Actions - Responsive */}
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 mb-6 border border-green-200">
              <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <FaCog className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-green-600" />
{t('superAdmin.quickActions')}
              </h2>
              <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 md:gap-4 place-items-stretch">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action.action)}
                    className={`w-full p-2 sm:p-3 md:p-4 rounded-lg border border-transparent hover:border-${action.color}-300 transition-all duration-200 hover:shadow ${
                      action.color === 'blue' ? 'bg-blue-50 hover:bg-blue-100' :
                      action.color === 'green' ? 'bg-green-50 hover:bg-green-100' :
                      action.color === 'purple' ? 'bg-purple-50 hover:bg-purple-100' :
                      action.color === 'orange' ? 'bg-orange-50 hover:bg-orange-100' :
                      action.color === 'teal' ? 'bg-teal-50 hover:bg-teal-100' :
                      'bg-red-50 hover:bg-red-100'
                    }`}
                  >
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mx-auto mb-1 sm:mb-2 md:mb-3 rounded-full flex items-center justify-center ${
                      action.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                      action.color === 'green' ? 'bg-green-100 text-green-600' :
                      action.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                      action.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                      action.color === 'teal' ? 'bg-teal-100 text-teal-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {action.icon}
                    </div>
                    <p className="text-xs sm:text-xs md:text-sm font-medium text-gray-700 text-center leading-tight">{action.title}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Charts Section */}
            {!loading && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {/* Questions Status Distribution Chart */}
                <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-gray-800 flex items-center">
                      <FaChartPie className="w-4 h-4 mr-2 text-purple-600" />
                      {t('superAdmin.questionsStatus')}
                    </h3>
                  </div>
                  <div className="h-64">
                    <Doughnut
                      data={{
                        labels: [t('superAdmin.answered'), t('superAdmin.inProgress')],
                        datasets: [{
                          data: [
                            dashboardData.questions?.answered || 0,
                            dashboardData.questions?.inProgress || 0
                          ],
                          backgroundColor: [
                            'rgba(34, 197, 94, 0.8)',
                            'rgba(245, 158, 11, 0.8)'
                          ],
                          borderColor: [
                            'rgba(34, 197, 94, 1)',
                            'rgba(245, 158, 11, 1)'
                          ],
                          borderWidth: 2
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                            labels: {
                              padding: 15,
                              font: {
                                size: 12
                              }
                            }
                          },
                          tooltip: {
                            callbacks: {
                              label: function(context) {
                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                                return `${context.label}: ${context.parsed} (${percentage}%)`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Disclosure Topics by Standard Chart */}
                {dashboardData.disclosureTopics?.byStandard && Object.keys(dashboardData.disclosureTopics.byStandard).length > 0 && (
                  <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-800 flex items-center">
                        <FaChartBar className="w-4 h-4 mr-2 text-cyan-600" />
                        {t('superAdmin.topicsByStandard')}
                      </h3>
                    </div>
                    <div className="h-64">
                      <Bar
                        data={{
                          labels: Object.keys(dashboardData.disclosureTopics.byStandard),
                          datasets: [{
                            label: t('superAdmin.topics'),
                            data: Object.values(dashboardData.disclosureTopics.byStandard),
                            backgroundColor: 'rgba(6, 182, 212, 0.8)',
                            borderColor: 'rgba(6, 182, 212, 1)',
                            borderWidth: 1,
                            borderRadius: 4
                          }]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: false
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${t('superAdmin.topics')}: ${context.parsed.y}`;
                                }
                              }
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                stepSize: 1
                              },
                              grid: {
                                color: 'rgba(0, 0, 0, 0.05)'
                              }
                            },
                            x: {
                              grid: {
                                display: false
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default HomeForSuperAdmin;
